import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';

const EMPTY_LINE = {
  cost_code_vuid: '',
  labor_class: '',
  cost_rate: '',
  billing_rate: '',
  effective_date: '',
  overtime_cost_rate: '',
  overtime_billing_rate: '',
};

const lineNeedsRates = (line) => (line?.status || '') === 'incomplete';

const formatRate = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  const numeric = parseFloat(value);
  if (Number.isNaN(numeric)) return '';
  return numeric.toFixed(2);
};

const displayLineRate = (line, field) => {
  if (lineNeedsRates(line)) return '—';
  return formatRate(line[field]);
};

const displayLineDate = (line) => {
  if (lineNeedsRates(line)) return 'Required';
  return line.effective_date || '';
};

const PencilIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    <path d="M2.695 14.763l-1.262 3.154a.5.5 0 00.65.65l3.155-1.262a4 4 0 001.343-.885L17.5 5.5a2.121 2.121 0 00-3-3L3.58 13.42a4 4 0 00-.885 1.343z" />
  </svg>
);

const TrashIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path fillRule="evenodd" d="M10 3h4a1 1 0 011 1v1h4a1 1 0 110 2h-.42l-.86 12.15A2.5 2.5 0 0115.23 21H8.77a2.5 2.5 0 01-2.49-2.35L5.42 7H5a1 1 0 110-2h4V4a1 1 0 011-1zm1 1v1h2V4h-2zM9.25 9a.75.75 0 01.75.75v7.5a.75.75 0 01-1.5 0v-7.5A.75.75 0 019.25 9zm2.75 0a.75.75 0 01.75.75v7.5a.75.75 0 01-1.5 0v-7.5A.75.75 0 0112 9zm2.75 0a.75.75 0 01.75.75v7.5a.75.75 0 01-1.5 0v-7.5A.75.75 0 0114.75 9z" clipRule="evenodd" />
  </svg>
);

const LaborRateSchedules = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const projectVuid = new URLSearchParams(location.search).get('project');
  const isProcorePreview = location.pathname.startsWith('/procore');

  const [projects, setProjects] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [budgetCostCodes, setBudgetCostCodes] = useState([]);
  const [budgetCostTypes, setBudgetCostTypes] = useState([]);
  const [bulkCostTypeVuid, setBulkCostTypeVuid] = useState('');
  const [laborClassOptions, setLaborClassOptions] = useState([]);
  const [newLine, setNewLine] = useState(EMPTY_LINE);
  const [editLine, setEditLine] = useState(null);
  const [editingLineVuid, setEditingLineVuid] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const laborClasses = laborClassOptions.length
    ? laborClassOptions
    : (schedule?.labor_classes || []);

  const selectedProject = useMemo(
    () => projects.find((project) => project.vuid === projectVuid),
    [projects, projectVuid]
  );

  useEffect(() => {
    axios.get(`${API_BASE_URL}/api/projects`)
      .then((response) => setProjects(Array.isArray(response.data) ? response.data : []))
      .catch(() => setProjects([]));
  }, []);

  useEffect(() => {
    if (!projectVuid) {
      setSchedule(null);
      setBudgetCostCodes([]);
      setBudgetCostTypes([]);
      setBulkCostTypeVuid('');
      setLaborClassOptions([]);
      setEditingLineVuid(null);
      setEditLine(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const codesPromise = axios.get(`${API_BASE_URL}/api/labor-rate-schedules/budget-cost-codes`, {
          params: { project_vuid: projectVuid },
        });
        const typesPromise = axios.get(`${API_BASE_URL}/api/labor-rate-schedules/budget-cost-types`, {
          params: { project_vuid: projectVuid },
        });
        const classesPromise = axios.get(`${API_BASE_URL}/api/labor-classes`, {
          params: { status: 'active' },
        });
        let scheduleRes;
        try {
          scheduleRes = await axios.post(`${API_BASE_URL}/api/labor-rate-schedules`, { project_vuid: projectVuid });
        } catch (createErr) {
          scheduleRes = await axios.get(`${API_BASE_URL}/api/labor-rate-schedules`, {
            params: { project_vuid: projectVuid },
          });
          const existing = Array.isArray(scheduleRes.data) ? scheduleRes.data[0] : scheduleRes.data;
          if (!existing) throw createErr;
          scheduleRes = { data: existing };
        }
        const codesRes = await codesPromise;
        const typesRes = await typesPromise;
        const classesRes = await classesPromise;
        if (cancelled) return;
        const classNames = Array.isArray(classesRes.data)
          ? classesRes.data.map((item) => item.name).filter(Boolean)
          : [];
        const types = Array.isArray(typesRes.data) ? typesRes.data : [];
        const laborType = types.find((item) => {
          const name = String(item.cost_type || '').toLowerCase();
          const abbr = String(item.abbreviation || '').toLowerCase();
          return name === 'labor' || abbr === 'l' || abbr === 'lab';
        });
        setSchedule(scheduleRes.data);
        setBudgetCostCodes(Array.isArray(codesRes.data) ? codesRes.data : []);
        setBudgetCostTypes(types);
        setBulkCostTypeVuid(laborType?.vuid || types[0]?.vuid || '');
        setLaborClassOptions(classNames);
        setEditingLineVuid(null);
        setEditLine(null);
        setNewLine((prev) => ({
          ...EMPTY_LINE,
          labor_class: classNames[0] || '',
          cost_code_vuid: prev.cost_code_vuid,
        }));
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || err.message || 'Failed to load labor rate schedule');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [projectVuid]);

  const laborClassChoices = (currentValue) => {
    const options = [...laborClasses];
    if (currentValue && !options.includes(currentValue)) {
      options.unshift(currentValue);
    }
    return options;
  };

  const costCodeChoices = (currentVuid, currentCostCode) => {
    const options = [...budgetCostCodes];
    if (currentVuid && !options.some((code) => code.vuid === currentVuid)) {
      options.unshift({
        vuid: currentVuid,
        code: currentCostCode?.code || 'Unknown',
        description: currentCostCode?.description || '',
      });
    }
    return options;
  };

  const lineToForm = (line) => {
    const incomplete = lineNeedsRates(line);
    const rateValue = (value) => {
      if (incomplete && (value === null || value === undefined || Number(value) === 0)) {
        return '';
      }
      return value === null || value === undefined ? '' : String(value);
    };
    return {
      cost_code_vuid: line.cost_code_vuid || '',
      labor_class: line.labor_class || '',
      cost_rate: rateValue(line.cost_rate),
      billing_rate: rateValue(line.billing_rate),
      effective_date: line.effective_date || '',
      overtime_cost_rate: line.overtime_cost_rate === null || line.overtime_cost_rate === undefined ? '' : String(line.overtime_cost_rate),
      overtime_billing_rate: line.overtime_billing_rate === null || line.overtime_billing_rate === undefined ? '' : String(line.overtime_billing_rate),
    };
  };

  const costCodeLabel = (costCodeVuid, costCode) => {
    if (costCode?.code) {
      return costCode.description ? `${costCode.code} - ${costCode.description}` : costCode.code;
    }
    const match = budgetCostCodes.find((item) => item.vuid === costCodeVuid);
    if (match?.code) {
      return match.description ? `${match.code} - ${match.description}` : match.code;
    }
    return 'Unknown cost code';
  };

  const handleBulkAddByCostType = async () => {
    if (!schedule) return;
    if (!bulkCostTypeVuid) {
      setError('Select a cost type to bulk add cost codes.');
      return;
    }
    const laborClass = newLine.labor_class || laborClasses[0] || '';
    setSaving(true);
    setError('');
    setSuccessMessage('');
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/labor-rate-schedules/${schedule.vuid}/lines/bulk`,
        {
          cost_type_vuid: bulkCostTypeVuid,
          labor_class: laborClass,
          cost_rate: newLine.cost_rate,
          billing_rate: newLine.billing_rate,
          effective_date: newLine.effective_date,
          overtime_cost_rate: newLine.overtime_cost_rate,
          overtime_billing_rate: newLine.overtime_billing_rate,
        }
      );
      const createdLines = Array.isArray(response.data?.lines) ? response.data.lines : [];
      if (createdLines.length) {
        setSchedule((prev) => ({
          ...prev,
          lines: [...(prev?.lines || []), ...createdLines],
        }));
      }
      const createdCount = Number(response.data?.created_count || createdLines.length);
      const skippedCount = Number(response.data?.skipped_count || 0);
      const typeName = response.data?.cost_type || 'selected cost type';
      const createdIncomplete = createdLines.some(lineNeedsRates);
      if (createdCount === 0) {
        setSuccessMessage(
          skippedCount
            ? `All ${typeName} cost codes are already on this schedule for ${laborClass}.`
            : `No budget cost codes use ${typeName}.`
        );
      } else {
        setSuccessMessage(
          `Added ${createdCount} ${typeName} cost code${createdCount === 1 ? '' : 's'}` +
            (skippedCount ? ` (${skippedCount} already on the schedule)` : '') +
            (createdIncomplete
              ? '. Enter cost rate, billing rate, and effective date on those rows before they can be used.'
              : '.')
        );
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to bulk add cost codes');
    } finally {
      setSaving(false);
    }
  };

  const handleAddLine = async () => {
    if (!schedule) return;
    if (!newLine.cost_code_vuid || !newLine.labor_class || !newLine.effective_date || newLine.cost_rate === '' || newLine.billing_rate === '') {
      setError('Cost code, labor class, effective date, cost rate, and billing rate are required.');
      return;
    }
    setSaving(true);
    setError('');
    setSuccessMessage('');
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/labor-rate-schedules/${schedule.vuid}/lines`,
        newLine
      );
      setSchedule((prev) => ({
        ...prev,
        lines: [...(prev?.lines || []), response.data],
      }));
      setNewLine({
        ...EMPTY_LINE,
        labor_class: laborClasses[0] || '',
      });
      setSuccessMessage('Rate added.');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to add rate');
    } finally {
      setSaving(false);
    }
  };

  const handleStartEdit = (line) => {
    setEditingLineVuid(line.vuid);
    setEditLine(lineToForm(line));
    setError('');
    setSuccessMessage('');
  };

  const handleCancelEdit = () => {
    setEditingLineVuid(null);
    setEditLine(null);
  };

  const handleSaveLine = async () => {
    if (!editLine || !editingLineVuid) return;
    if (!editLine.cost_code_vuid || !editLine.labor_class || !editLine.effective_date || editLine.cost_rate === '' || editLine.billing_rate === '') {
      setError('Cost code, labor class, effective date, cost rate, and billing rate are required.');
      return;
    }
    setSaving(true);
    setError('');
    setSuccessMessage('');
    try {
      const response = await axios.put(
        `${API_BASE_URL}/api/labor-rate-schedules/lines/${editingLineVuid}`,
        editLine
      );
      setSchedule((prev) => ({
        ...prev,
        lines: (prev?.lines || []).map((line) => (line.vuid === editingLineVuid ? response.data : line)),
      }));
      setEditingLineVuid(null);
      setEditLine(null);
      setSuccessMessage('Rate updated.');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to update rate');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLine = async (lineVuid) => {
    setSaving(true);
    setError('');
    setSuccessMessage('');
    try {
      await axios.delete(`${API_BASE_URL}/api/labor-rate-schedules/lines/${lineVuid}`);
      setSchedule((prev) => ({
        ...prev,
        lines: (prev?.lines || []).filter((line) => line.vuid !== lineVuid),
      }));
      if (editingLineVuid === lineVuid) {
        setEditingLineVuid(null);
        setEditLine(null);
      }
      setSuccessMessage('Rate deleted.');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to delete rate');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="labor-rate-schedule-page-header">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-gray-900">Labor Rate Schedule</h1>
          <p className="text-sm text-gray-600 mt-1">
            Enter cost and billing rates by budget cost code and labor class. These rates will feed labor calculations later.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow border border-gray-200 p-4 mt-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">Project</label>
        <select
          className="border border-gray-300 rounded px-3 py-2 text-sm min-w-[320px]"
          value={projectVuid || ''}
          onChange={(e) => {
            const value = e.target.value;
            navigate(value ? `/labor-rate-schedules?project=${value}` : '/labor-rate-schedules');
          }}
        >
          <option value="">Select a project</option>
          {projects.map((project) => (
            <option key={project.vuid} value={project.vuid}>
              {project.project_number} - {project.project_name}
            </option>
          ))}
        </select>
        {selectedProject ? (
          <p className="text-xs text-gray-500 mt-2">
            Cost codes come from the project budget for {selectedProject.project_number} - {selectedProject.project_name}.
          </p>
        ) : null}
      </div>
      </div>

      {error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4">{error}</div>
      ) : null}
      {successMessage ? (
        <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg p-4">{successMessage}</div>
      ) : null}
      {projectVuid && !loading && (schedule?.lines || []).some(lineNeedsRates) ? (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-4">
          Highlighted rows still need a cost rate, billing rate, and effective date. They will not be used for labor costs until those fields are entered.
        </div>
      ) : null}

      {!projectVuid ? (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center text-gray-500">
          Select a project to manage its labor rate schedule.
        </div>
      ) : loading ? (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center text-gray-500">
          Loading labor rate schedule…
        </div>
      ) : (
        <>
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label htmlFor="bulk-cost-type" className="block text-sm font-medium text-gray-700 mb-1">
                Bulk add by cost type
              </label>
              <select
                id="bulk-cost-type"
                className="border border-gray-300 rounded px-3 py-2 text-sm min-w-[220px]"
                value={bulkCostTypeVuid}
                onChange={(e) => setBulkCostTypeVuid(e.target.value)}
                disabled={budgetCostTypes.length === 0}
              >
                {budgetCostTypes.length === 0 ? (
                  <option value="">No cost types on this budget</option>
                ) : (
                  budgetCostTypes.map((costType) => (
                    <option key={costType.vuid} value={costType.vuid}>
                      {costType.abbreviation
                        ? `${costType.cost_type} (${costType.abbreviation})`
                        : costType.cost_type}
                    </option>
                  ))
                )}
              </select>
            </div>
            <button
              type="button"
              className="btn-claret"
              onClick={handleBulkAddByCostType}
              disabled={
                saving ||
                Boolean(editingLineVuid) ||
                budgetCostTypes.length === 0 ||
                laborClasses.length === 0
              }
              data-tooltip="Add every budget cost code that uses the selected cost type"
            >
              Add All Cost Codes
            </button>
            <p className="text-xs text-gray-500 max-w-xl">
              Adds every budget cost code for the selected type. Enter cost rate, billing rate, and effective date on each new row before it can be used. Cost codes already on the schedule for the same labor class are skipped.
            </p>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow border border-gray-200 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cost Code</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Labor Class</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Cost Rate</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Billing Rate</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Effective Date</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">OT Cost Rate</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">OT Billing Rate</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(schedule?.lines || []).map((line) => (
                editingLineVuid === line.vuid ? (
                  <tr key={line.vuid} className="bg-amber-50">
                    <td className="px-4 py-3">
                      <select
                        className="w-full border border-gray-300 rounded px-2 py-2 text-sm"
                        value={editLine.cost_code_vuid}
                        onChange={(e) => setEditLine({ ...editLine, cost_code_vuid: e.target.value })}
                        disabled={budgetCostCodes.length === 0}
                      >
                        <option value="">Select cost code</option>
                        {costCodeChoices(line.cost_code_vuid, line.cost_code).map((code) => (
                          <option key={code.vuid} value={code.vuid}>
                            {code.description ? `${code.code} - ${code.description}` : code.code}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        className="w-full border border-gray-300 rounded px-2 py-2 text-sm"
                        value={editLine.labor_class}
                        onChange={(e) => setEditLine({ ...editLine, labor_class: e.target.value })}
                        disabled={laborClassChoices(line.labor_class).length === 0}
                      >
                        {laborClassChoices(line.labor_class).map((laborClass) => (
                          <option key={laborClass} value={laborClass}>{laborClass}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="text"
                        inputMode="decimal"
                        className="w-full border border-gray-300 rounded px-2 py-2 text-sm text-right"
                        placeholder="0.00"
                        value={editLine.cost_rate}
                        onChange={(e) => setEditLine({ ...editLine, cost_rate: e.target.value })}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="text"
                        inputMode="decimal"
                        className="w-full border border-gray-300 rounded px-2 py-2 text-sm text-right"
                        placeholder="0.00"
                        value={editLine.billing_rate}
                        onChange={(e) => setEditLine({ ...editLine, billing_rate: e.target.value })}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="date"
                        className="w-full border border-gray-300 rounded px-2 py-2 text-sm"
                        value={editLine.effective_date}
                        onChange={(e) => setEditLine({ ...editLine, effective_date: e.target.value })}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="text"
                        inputMode="decimal"
                        className="w-full border border-gray-300 rounded px-2 py-2 text-sm text-right"
                        placeholder="0.00"
                        value={editLine.overtime_cost_rate}
                        onChange={(e) => setEditLine({ ...editLine, overtime_cost_rate: e.target.value })}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="text"
                        inputMode="decimal"
                        className="w-full border border-gray-300 rounded px-2 py-2 text-sm text-right"
                        placeholder="0.00"
                        value={editLine.overtime_billing_rate}
                        onChange={(e) => setEditLine({ ...editLine, overtime_billing_rate: e.target.value })}
                      />
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        type="button"
                        className="btn-claret mr-2"
                        onClick={handleSaveLine}
                        disabled={saving}
                        data-tooltip="Save rate"
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        className="btn-claret"
                        onClick={handleCancelEdit}
                        disabled={saving}
                        data-tooltip="Cancel edit"
                      >
                        Cancel
                      </button>
                    </td>
                  </tr>
                ) : (
                  <tr key={line.vuid} className={lineNeedsRates(line) ? 'bg-amber-50' : undefined}>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <div>{costCodeLabel(line.cost_code_vuid, line.cost_code)}</div>
                      {lineNeedsRates(line) ? (
                        <div className="text-xs font-medium text-amber-800 mt-1">Needs rates</div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">{line.labor_class}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">{displayLineRate(line, 'cost_rate')}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">{displayLineRate(line, 'billing_rate')}</td>
                    <td className={`px-4 py-3 text-sm ${lineNeedsRates(line) ? 'text-amber-800 font-medium' : 'text-gray-900'}`}>
                      {displayLineDate(line)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatRate(line.overtime_cost_rate)}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatRate(line.overtime_billing_rate)}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <div className="inline-flex items-center justify-end gap-2">
                      {isProcorePreview ? (
                        <button
                          type="button"
                          className="pc-row-icon-btn"
                          onClick={() => handleStartEdit(line)}
                          disabled={saving || Boolean(editingLineVuid)}
                          data-tooltip="Edit rate"
                          aria-label="Edit rate"
                        >
                          <PencilIcon />
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn-claret"
                          onClick={() => handleStartEdit(line)}
                          disabled={saving || Boolean(editingLineVuid)}
                          data-tooltip="Edit rate"
                        >
                          Edit
                        </button>
                      )}
                      {isProcorePreview ? (
                        <button
                          type="button"
                          className="pc-row-icon-btn"
                          onClick={() => handleDeleteLine(line.vuid)}
                          disabled={saving || Boolean(editingLineVuid)}
                          data-tooltip="Delete rate"
                          aria-label="Delete rate"
                        >
                          <TrashIcon />
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn-claret"
                          onClick={() => handleDeleteLine(line.vuid)}
                          disabled={saving || Boolean(editingLineVuid)}
                          data-tooltip="Delete rate"
                        >
                          Delete
                        </button>
                      )}
                      </div>
                    </td>
                  </tr>
                )
              ))}
              {(schedule?.lines || []).length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-6 text-center text-sm text-gray-500">
                    {budgetCostCodes.length === 0
                      ? 'Add cost codes to this project budget before entering labor rates.'
                      : laborClasses.length === 0
                        ? 'Add labor classes in Master Data before entering labor rates.'
                        : 'No labor rates yet. Add a rate below.'}
                  </td>
                </tr>
              ) : null}
              <tr className="bg-gray-50">
                <td className="px-4 py-3">
                  <select
                    className="w-full border border-gray-300 rounded px-2 py-2 text-sm"
                    value={newLine.cost_code_vuid}
                    onChange={(e) => setNewLine({ ...newLine, cost_code_vuid: e.target.value })}
                    disabled={budgetCostCodes.length === 0}
                  >
                    <option value="">Select cost code</option>
                    {budgetCostCodes.map((code) => (
                      <option key={code.vuid} value={code.vuid}>
                        {code.description ? `${code.code} - ${code.description}` : code.code}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <select
                    className="w-full border border-gray-300 rounded px-2 py-2 text-sm"
                    value={newLine.labor_class}
                    onChange={(e) => setNewLine({ ...newLine, labor_class: e.target.value })}
                    disabled={laborClasses.length === 0}
                  >
                    {laborClasses.length === 0 ? (
                      <option value="">No labor classes</option>
                    ) : null}
                    {laborClasses.map((laborClass) => (
                      <option key={laborClass} value={laborClass}>{laborClass}</option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <input
                    type="text"
                    inputMode="decimal"
                    className="w-full border border-gray-300 rounded px-2 py-2 text-sm text-right"
                    placeholder="0.00"
                    value={newLine.cost_rate}
                    onChange={(e) => setNewLine({ ...newLine, cost_rate: e.target.value })}
                  />
                </td>
                <td className="px-4 py-3">
                  <input
                    type="text"
                    inputMode="decimal"
                    className="w-full border border-gray-300 rounded px-2 py-2 text-sm text-right"
                    placeholder="0.00"
                    value={newLine.billing_rate}
                    onChange={(e) => setNewLine({ ...newLine, billing_rate: e.target.value })}
                  />
                </td>
                <td className="px-4 py-3">
                  <input
                    type="date"
                    className="w-full border border-gray-300 rounded px-2 py-2 text-sm"
                    value={newLine.effective_date}
                    onChange={(e) => setNewLine({ ...newLine, effective_date: e.target.value })}
                  />
                </td>
                <td className="px-4 py-3">
                  <input
                    type="text"
                    inputMode="decimal"
                    className="w-full border border-gray-300 rounded px-2 py-2 text-sm text-right"
                    placeholder="0.00"
                    value={newLine.overtime_cost_rate}
                    onChange={(e) => setNewLine({ ...newLine, overtime_cost_rate: e.target.value })}
                  />
                </td>
                <td className="px-4 py-3">
                  <input
                    type="text"
                    inputMode="decimal"
                    className="w-full border border-gray-300 rounded px-2 py-2 text-sm text-right"
                    placeholder="0.00"
                    value={newLine.overtime_billing_rate}
                    onChange={(e) => setNewLine({ ...newLine, overtime_billing_rate: e.target.value })}
                  />
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    type="button"
                    className="btn-claret"
                    onClick={handleAddLine}
                    disabled={saving || Boolean(editingLineVuid) || budgetCostCodes.length === 0 || laborClasses.length === 0}
                    data-tooltip="Add labor rate"
                  >
                    Add Rate
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );
};

export default LaborRateSchedules;
